import { useEffect, useMemo, useState } from "react";

import moment from "moment";
import { twMerge } from "tailwind-merge";

import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import {
  CellDateFormatter,
  CellMetricFormatter,
  CellNumberFormatter,
  CellTextFormatter,
  HeaderWithInfoIcon,
  Table,
  createColumnHelper,
} from "@components/v2/table";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummary,
  type FetchOrderSummaryVariables,
  type OrdersSortingField,
  type OrdersSortingOrder,
  type SubscriptionTypeEnum,
} from "@north-beam/nb-client-side";
import { FETCH_ORDER_SUMMARY } from "@pages/settings/queries";
import { type SortingState } from "@tanstack/react-table";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import {
  useOrdersPageFilters,
  useOrdersPagination,
} from "./hooks/use-orders-filters";
import { useOrdersTableColumnWidths } from "./hooks/use-orders-table-column-widths";
import AttributionCellFormatter from "./table-cells/attribution-cell-formatter";
import BadgeCellFormatter from "./table-cells/badge-cell-formatter";
import CustomerTypeCellFormatter from "./table-cells/customer-type-cell-formatter";
import OrderIdFormatter from "./table-cells/order-id-formatter";
import { type QuickFilter } from "./types";

export type OrderTableProps = {
  totalCount: number;
  isLoading: boolean;
  orders: {
    orderId: string;
    orderNumber: string;
    date: Date;
    orderValue: number;
    discountValue: number;
    shippingValue: number;
    taxValue: number;
    refundValue: number;
    numberOfOriginalItems: number;
    customerId: string | null;
    customerType: string;
    numberOfTouchpoints: number;
    isAttributed: string;
    tags: string[];
    sourceName: string[];
    customerTags: string[];
    discountCodes: string[];
    productsPurchased: string[];
    subscriptionType: SubscriptionTypeEnum | null;
  }[];
};

export type OrdersV2TableProps = {
  className?: string;
  tableClassName?: string;
  selectedQuickFilter: QuickFilter;
};

export function OrdersTable({
  className,
  tableClassName,
  selectedQuickFilter,
}: OrdersV2TableProps) {
  const { user } = useUser();
  const filterOptions = useOrdersPageFilters();
  const { addQueryMetadata } = useQueryMetadata();

  const [pagination, setPagination] = useOrdersPagination();

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (selectedQuickFilter.sorting.length > 0) {
      setSorting(selectedQuickFilter.sorting);
    }
  }, [selectedQuickFilter]);

  const { data: orderSummaryData, loading } = useNorthbeamQuery<
    FetchOrderSummary,
    FetchOrderSummaryVariables
  >(FETCH_ORDER_SUMMARY, {
    variables: {
      filterOptions: filterOptions,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sorting:
        sorting?.length > 0
          ? {
              sortingField: sorting[0].id as OrdersSortingField,
              sortingOrder: sorting[0].desc
                ? ("desc" as OrdersSortingOrder)
                : ("asc" as OrdersSortingOrder),
            }
          : undefined,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders Table",
        metadata: data.me.orders.queryMetadata,
      });
    },
  });

  const orders = useMemo(() => {
    return (
      orderSummaryData?.me.orders.data.map((order) => ({
        conversionEventId: order.conversionEventId,
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        date: new Date(order.occurredAt.value),
        orderValue: order.revenueInDollars,
        discountValue: order.discountValue,
        shippingValue: order.shippingValue,
        taxValue: order.taxValue,
        refundValue: order.refundAmountInDollars,
        numberOfOriginalItems: order.products.reduce(
          (acc, curr) => acc + curr.quantity,
          0,
        ),
        customerId: order.customerId,
        customerType: order.orderType,
        numberOfTouchpoints: order.newNumberOfTouchpoints,
        isAttributed: order.attributed ? "Yes" : "No",
        tags: order.orderTags,
        sourceName: order.sourceName,
        customerTags: order.customerTags,
        subscriptionType: order.subscriptionType,
        productsPurchased: order.products.map((p) => p.title),
        discountCodes: order.discountCodes,
      })) || []
    );
  }, [orderSummaryData]);

  const columnHelper = createColumnHelper<OrderTableProps["orders"][0]>();
  const prefs = usePrefs();
  const isAnon = prefs.isDemoMode;

  const columns = useMemo(
    () => [
      columnHelper.accessor({
        accessorKey: (r) => r.orderId,
        columnDef: {
          id: "orderId",
          cell: (info) => <OrderIdFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Order ID" header={header} />;
          },
          enableSorting: false,
          sticky: true,
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.orderNumber,
        columnDef: {
          id: "orderNumber",
          cell: (info) => (
            <CellNumberFormatter
              cell={info}
              formattedValue={
                isAnon
                  ? generateAnonBase64Hash({
                      value: info.getValue() as string,
                      prefix: "orderNumber",
                      doAnon: isAnon,
                    })
                  : undefined
              }
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Order Number" header={header} />;
          },
          enableSorting: false,
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.date,
        columnDef: {
          id: "date",
          cell: (info) => (
            <CellDateFormatter
              value={moment(info.getValue()).format("MM/DD/YYYY")}
              cell={info}
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Date" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.orderValue,
        columnDef: {
          id: "orderValue",
          cell: (info) => <CellMetricFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Order" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.discountValue,
        columnDef: {
          id: "discountValue",
          cell: (info) => <CellMetricFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Discount" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.shippingValue,
        columnDef: {
          id: "shippingValue",
          cell: (info) => <CellMetricFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Shipping" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.taxValue,
        columnDef: {
          id: "taxValue",
          cell: (info) => <CellMetricFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Tax" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.refundValue,
        columnDef: {
          id: "refundValue",
          cell: (info) => <CellMetricFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Refund" header={header} />;
          },
        },
      }),

      columnHelper.accessor({
        accessorKey: (r) => r.customerId,
        columnDef: {
          enableSorting: false,
          id: "customerId",
          cell: (info) => (
            <CellTextFormatter
              value={
                isAnon
                  ? generateAnonBase64Hash({
                      value: info.getValue() as string,
                      prefix: "customerId",
                      doAnon: isAnon,
                    })
                  : info.getValue() || "-"
              }
              cell={info}
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Customer ID" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.numberOfTouchpoints,
        columnDef: {
          id: "numberOfTouchpoints",
          cell: (info) => <CellNumberFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Touchpoints" header={header} />;
          },
        },
      }),

      columnHelper.accessor({
        accessorKey: (r) => r.numberOfOriginalItems,
        columnDef: {
          id: "numberOfOriginalItems",
          cell: (info) => {
            const value = info.row.original.productsPurchased;
            return <BadgeCellFormatter cell={info} items={value} />;
          },
          header: (header) => {
            return <HeaderWithInfoIcon title="Products" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.customerType,
        columnDef: {
          id: "customerType",
          cell: (info) => <CustomerTypeCellFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Customer Type" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.isAttributed,
        columnDef: {
          id: "isAttributed",
          enableSorting: false,
          cell: (info) => <AttributionCellFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Attributed?" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.tags,
        columnDef: {
          id: "tags",
          enableSorting: false,
          cell: (info) => <BadgeCellFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Tags" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.customerTags,
        columnDef: {
          id: "customerTags",
          enableSorting: false,
          cell: (info) => <BadgeCellFormatter cell={info} />,
          header: (header) => {
            return <HeaderWithInfoIcon title="Customer Tags" header={header} />;
          },
        },
      }),
      columnHelper.accessor({
        accessorKey: (r) => r.sourceName,
        columnDef: {
          enableSorting: false,
          id: "sourceName",
          cell: (info) => (
            <CellTextFormatter
              value={info.getValue()?.join(",") || "-"}
              cell={info}
            />
          ),
          header: (header) => {
            return <HeaderWithInfoIcon title="Source" header={header} />;
          },
        },
      }),
      ...(user.featureFlags.showSubscriptionOrderTags
        ? [
            columnHelper.accessor({
              accessorKey: (r) => r.subscriptionType,
              columnDef: {
                enableSorting: false,
                id: "subscriptionType",
                cell: (info) => (
                  <CellTextFormatter
                    value={info.getValue() || "-"}
                    cell={info}
                  />
                ),
                header: (header) => {
                  return (
                    <HeaderWithInfoIcon title="Subscription" header={header} />
                  );
                },
              },
            }),
          ]
        : []),
      columnHelper.accessor({
        accessorKey: (r) => r.discountCodes,
        columnDef: {
          enableSorting: false,
          id: "discountCodes",
          cell: (info) => (
            <CellTextFormatter
              value={info.getValue()?.join("-") || "-"}
              cell={info}
            />
          ),
          header: (header) => {
            return (
              <HeaderWithInfoIcon title="Discount Codes" header={header} />
            );
          },
        },
      }),
    ],
    [columnHelper, user.featureFlags, isAnon],
  );
  const { columnWidths, setColumnWidths } = useOrdersTableColumnWidths();

  return (
    <div className={twMerge("flex flex-col gap-4", className)}>
      <Table
        className={tableClassName}
        columns={columns}
        rows={orders}
        loading={loading}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={orderSummaryData?.me.orders.totalCount || 0}
        sortingState={sorting}
        onSortingChange={setSorting}
        multiSort={false}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
      />
    </div>
  );
}
